import { useCallback, useEffect, useMemo, useState } from 'react';
import useCollectionSnapshot from './useCollectionSnapshot';
import { defaultPhaseViewIds, filterCurrentPhaseLearners, filterRecordedPhases, resolveWorkingScope } from '../services/domain/phaseView';

import { filterClassScope } from '../services/domain/classScope';

const EMPTY = Object.freeze([]);
function readChoice(key) {
  if (!key) return null;
  try {
    const saved = localStorage.getItem(key);
    return saved || null;
  } catch { return null; }
}

/** One school/actor-specific working scope, including phase choices. */
export default function usePhaseScope({ schoolId, actorId, role, classes, classIds, scopeLoading, scopeError, scopeFromCache, scopeConfirmed, retryScope }) {
  const enabled = Boolean(schoolId && actorId && role && !['parent', 'student', 'learner'].includes(role));
  const identity = useMemo(() => [actorId, role], [actorId, role]);
  const phasesRead = useCollectionSnapshot('schoolPhases', EMPTY, { enabled, identity, retainConfirmed: true });
  const gradesRead = useCollectionSnapshot('grades', EMPTY, { enabled, identity, retainConfirmed: true });
  const key = enabled ? `swan_working_scope:${schoolId}:${actorId}:${role}` : null;
  const [choice, setChoice] = useState(() => ({ key, value: readChoice(key) }));
  useEffect(() => { setChoice({ key, value: readChoice(key) }); }, [key]);
  const phases = useMemo(() => [...phasesRead.data].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)), [phasesRead.data]);
  const error = scopeError || phasesRead.error || gradesRead.error;
  const loading = enabled && (scopeLoading || phasesRead.loading || gradesRead.loading);
  const fromCache = enabled && (scopeFromCache || phasesRead.fromCache || gradesRead.fromCache);
  const confirmed = Boolean(scopeConfirmed && phasesRead.confirmed && gradesRead.confirmed);
  const ready = !enabled || (!loading && !error && (!fromCache || confirmed) && phasesRead.loaded && gradesRead.loaded);
  // `ready` may intentionally use a previously confirmed directory so ordinary
  // browsing does not collapse during a reconnect. Attendance is higher stakes:
  // callers use `current` to require a fresh server-backed directory before a
  // new register can be created.
  const current = !enabled || (ready && !fromCache);
  const directory = useMemo(() => ({ schoolId, classes, grades: gradesRead.data, phases }), [schoolId, classes, gradesRead.data, phases]);
  const myPhaseIds = useMemo(() => !ready || !enabled ? EMPTY
    : defaultPhaseViewIds({ ...directory, classIds }), [ready, enabled, directory, classIds]);
  const stored = choice.key === key ? choice.value : readChoice(key);
  const working = useMemo(() => resolveWorkingScope({ role, phases, myPhaseIds, choice: stored,
    hasAssignedScope: !ready || classIds?.size !== 0 }),
    [role, phases, myPhaseIds, stored, ready, classIds]);
  const { phaseIds, level } = working;
  const setScopeChoice = useCallback(value => {
    if (!working.options.some(option => option.key === value)) return;
    setChoice({ key, value });
    try { if (key) localStorage.setItem(key, value); } catch { /* The current view still works without persistent preferences. */ }
  }, [key, working.options]);
  const retryPhases = phasesRead.retry;
  const retryGrades = gradesRead.retry;
  const retry = useCallback(() => { retryPhases(); retryGrades(); retryScope?.(); }, [retryPhases, retryGrades, retryScope]);
  const filterRecords = useCallback(records => {
    if (!enabled) return records || [];
    if (!ready) return [];
    return level === 'scope' ? filterClassScope(records, classIds, classes)
      : filterRecordedPhases(records, phaseIds, schoolId);
  }, [enabled, ready, level, classIds, classes, phaseIds, schoolId]);
  const filterLearners = useCallback(learners => {
    if (!enabled) return learners || [];
    if (!ready) return [];
    return level === 'scope' ? filterClassScope(learners, classIds, classes)
      : filterCurrentPhaseLearners(learners, phaseIds, directory);
  }, [enabled, ready, level, classIds, classes, phaseIds, directory]);
  const filterClasses = useCallback(groups => {
    if (!enabled) return groups || [];
    if (!ready) return [];
    if (level === 'scope') return (groups || []).filter(group => classIds?.has(group.id));
    const selected = new Set(filterCurrentPhaseLearners((groups || []).map(group => ({ classId: group.id })), phaseIds, directory).map(learner => learner.classId));
    return (groups || []).filter(group => selected.has(group.id));
  }, [enabled, ready, level, classIds, phaseIds, directory]);
  return useMemo(() => ({ enabled, phases, myPhaseIds, ...working, setScopeChoice, ready, current, loading, error, fromCache, retry,
    filterRecords, filterLearners, filterClasses }),
  [enabled, phases, myPhaseIds, working, setScopeChoice, ready, current, loading, error, fromCache, retry, filterRecords, filterLearners, filterClasses]);

}
